import { Box, Typography, Link } from "@mui/material";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { Form, Formik } from 'formik';
import { LimitsForm } from './LimitsForm';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { limitsValidationSchema } from './LimitsValidationSchema';
import { flowLabelMap } from './../../../constants/lists'
import {
    LimitsProps,
    PrizeRatioType,
    limitsStateValues,
} from '../../../types/componentTypes/limits';
import '../CommonStyles.css';
import './Limits.css';
import { saveConfig } from '../../../utils/s3FileUtils';
import {
    clearParticipationLimits,
    clearAlwaysWinLimits,
    clearWinningLimits,
    getCheckerLambdasFromTempParams,
    getIWLimitsForConfig,
    shouldDeleteBlockEntry,
    removeCheckerLambdasWhenEmpty
} from "./helpers/limit-helpers";
import useLimitsInitialState from "./hooks/useLimitsInitialState";
import { API } from "aws-amplify";
import { updatePrizeCatalogueTable } from "../../../graphql/mutations";
import { prizeDataType } from "../EditPromotionPage";
import { PrizeTableItem } from "../../../types/componentTypes/PrizeTypes";
import { helpfulLinks } from "./constants/limit-constants";
import GenericSpinner from '../../../common/Spinners/GenericSpinner';
import { removeEmpty } from "../../../utils/configUtils";

const Limits = ({
    promotionMechanic,
    config,
    setConfig,
    setNotificationState,
    handleTabChange,
    prizesData,
    setPrizeData
}: LimitsProps) => {
    const urlParams = useParams();

    const [promoFlow, setPromoFlow] = useState<string>('')
    const [prizes, setPrizes] = useState<Array<prizeDataType>>(prizesData);
    const { initialState } = useLimitsInitialState(config, prizes);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

    useEffect(() => {
        setPromoFlow(flowLabelMap[promotionMechanic])
    }, [promoFlow, promotionMechanic])

    useEffect(() => {
        setPrizes(prizesData);
    }, [prizesData]);

    interface UpdatePrizeResult {
        updatePrizeCatalogueTable: {
            items?: PrizeTableItem[];
        };
    }

    const updatePrize = async (prize: PrizeRatioType) => {
        const winningRatio = Math.round(Number(prize.ratio) * 100);
        const response = await API.graphql({
            query: updatePrizeCatalogueTable, variables: {
                input: {
                    configuration_id: config.configurationId,
                    prize_id: prize.prizeId,
                    winning_ratio: winningRatio,
                }
            }
        }) as GraphQLResult<UpdatePrizeResult>;
        return response.data?.updatePrizeCatalogueTable;
    };

    const handleAlwaysWinPrizeRatios = async (values: limitsStateValues) => {
        try {
            let prizesToUpdate = {};
            let updateResults = [];

            const roundTo = (value: number, decimals: number = 4) =>
                Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);

            if (values.alwaysWinLimits.ratioWinning) {
                // preparing ratios winning object for selected prizes
                values.alwaysWinLimits.alwaysWinPrizeRatios.forEach((item: PrizeRatioType) => {
                    const [, id] = item.prizeId.split('/');
                    if (!id) { throw new Error('Prize id not found') }
                    const roundedRatio = roundTo(Number(item.ratio), 4);
                    Object.assign(prizesToUpdate, { [id.trim()]: roundedRatio });
                });

                // reset winning ratios to 0 for non-selected prizes
                prizes.forEach((item: prizeDataType) => {
                    if (!prizesToUpdate[item.prize_id]) {
                        Object.assign(prizesToUpdate, { [item.prize_id]: 0 });
                    }
                });
            } else {
                // delete winning ratio all prizes when ratio winning is disabled
                prizes.forEach((item: prizeDataType) => {
                    Object.assign(prizesToUpdate, { [item.prize_id]: 0 });
                });
                delete values.alwaysWinLimits.ratioWinning;
            }

            // update winning ratios to prize catalogue table
            if (Object.keys(prizesToUpdate).length !== 0) {
                for (const [prizeId, ratio] of Object.entries(prizesToUpdate)) {
                    const response = await updatePrize({ prizeId, ratio } as PrizeRatioType);
                    updateResults.push(response);
                }
            }

            setPrizeData(updateResults);
        } catch (e) {
            setNotificationState({
                open: true,
                title: 'Error',
                content: 'Prize winning ratio update failed.',
                level: "error"
            })
            return Promise.reject(new Error(
                `Failed to update winning ratio for prize: ${JSON.stringify(e)}`));
        }
    }

    const handleSubmit = (values: limitsStateValues) => {
        if (shouldDeleteBlockEntry(
            initialState.participationLimits,
            values.participationLimits,
            initialState.tempParams?.limits,
            values.tempParams?.limits
        )) {
            const params = values.flow[promoFlow].params || {};
            values.flow[promoFlow].params = {
                ...params,
                partLimitVer: params.partLimitVer ? params.partLimitVer + 1 : 1
            };
        }
        let updatedValues = clearParticipationLimits(values, promoFlow);
        updatedValues = clearAlwaysWinLimits(updatedValues, promoFlow);
        updatedValues = clearWinningLimits(updatedValues, promoFlow);

        const flowParams = {
            ...updatedValues.flow[promoFlow]?.params,
            ...updatedValues.participationLimits,
            ...updatedValues.alwaysWinLimits,
            ...(promoFlow !== 'promoEntry' && {
                ...updatedValues.winningLimits,
                ...getIWLimitsForConfig(updatedValues)
            })
        };

        updatedValues.flow = {
            ...updatedValues.flow,
            [promoFlow]: {
                ...updatedValues.flow[promoFlow],
                checkerLambdas: getCheckerLambdasFromTempParams(updatedValues, updatedValues.flow[promoFlow]?.checkerLambdas),
                params: flowParams,
            },
        };

        if (updatedValues.participationLimits?.minAge) {
            delete updatedValues?.configurationParameters?.additionalInformation?.minAge;
        }
        const flowConfig = updatedValues?.flow?.[promoFlow];
        if (flowConfig) {
            removeCheckerLambdasWhenEmpty(flowConfig);
        }

        [
            'tempParams',
            'participationLimits',
            'alwaysWinLimits',
            'selectedDateLimitOption',
            'winningLimits'
        ].forEach(field => delete updatedValues[field]);

        return removeEmpty(updatedValues);
    }

    return (
        <>
            <GenericSpinner showSpinner={showLoadingSpinner} />
            <Box className='edit-page-wrapper'>
                <Typography variant='h3'>
                    Limits
                </Typography>
            </Box>
            <Formik
                initialValues={initialState}
                validationSchema={limitsValidationSchema}
                enableReinitialize
                onSubmit={async (values) => {
                    try {
                        setShowLoadingSpinner(true);
                        if (values.alwaysWinLimits.hasOwnProperty('ratioWinning')) {
                            await handleAlwaysWinPrizeRatios(values);
                        };
                        const modifiedConfig = handleSubmit(values);
                        setConfig(modifiedConfig)
                        await saveConfig({ urlParams, submitData: modifiedConfig, setNotificationState });
                        setShowLoadingSpinner(false);
                        setNotificationState({
                            open: true,
                            title: 'Promotion updated successfully',
                            content: 'Promotion Changes were saved!',
                            level: "success"
                        })
                    } catch (e) {
                        console.error(e);
                    }
                }}
            >
                <Form>
                    <LimitsForm
                        handleTabChange={handleTabChange}
                        prizesData={prizesData}
                        promoFlow={promoFlow}
                    />
                </Form>
            </Formik >
            <Typography variant="h3" sx={{ marginTop: 'calc(100vh - 800px)' }} className="links-title">Helpful Links</Typography>
            <Box className='helpfulLinks'>
                {helpfulLinks[promotionMechanic].map((helpfulLink, i) =>
                    <Link key={i} target='_blank' href={helpfulLink.link} rel="noopener noreferrer">{helpfulLink.text}</Link>
                )}
            </Box>
        </>
    )
}

export { Limits };
